import { SHAREPOINT_CONFIG } from "@/config/sharepoint"
import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import apiClient from "@/services/api/apiClient"
import {
  CreateSharepointConnectionRequest,
  SharepointConnection,
  SharepointFolderSelection,
  SharepointFolderSelectionResponse,
  SharepointSyncStatusResponse,
  TriggerSyncRequest,
} from "@/types/sharepointConnection"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const CONNECTIONS_KEY = ["sharepointConnections"]
const BASE_PATH = "/integrations/sharepoint/connections"

const sharepointConnectionApi = {
  getConnection: async (): Promise<SharepointConnection | null> => {
    const response = await apiClient.get(BASE_PATH, {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    })
    return response.status === 404 ? null : response.data
  },

  createConnection: async (
    data: CreateSharepointConnectionRequest,
  ): Promise<SharepointConnection> => {
    const response = await apiClient.post(BASE_PATH, data)
    return response.data
  },

  deleteConnection: async (connectionId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/${connectionId}`)
  },

  addFolderSelections: async (
    connectionId: string,
    folders: SharepointFolderSelection[],
    microsoftAccessToken: string,
  ): Promise<SharepointFolderSelectionResponse[]> => {
    const response = await apiClient.post(
      `${BASE_PATH}/${connectionId}/selections`,
      { folders, microsoftAccessToken },
    )
    return response.data
  },

  removeFolderSelection: async (
    connectionId: string,
    selectionId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `${BASE_PATH}/${connectionId}/selections/${selectionId}`,
    )
  },

  triggerSync: async (
    connectionId: string,
    data: TriggerSyncRequest,
  ): Promise<SharepointSyncStatusResponse> => {
    const response = await apiClient.post(
      `${BASE_PATH}/${connectionId}/sync`,
      data,
    )
    return response.data
  },

  triggerAutoSync: async (
    connectionId: string,
    data: TriggerSyncRequest,
  ): Promise<SharepointSyncStatusResponse> => {
    const response = await apiClient.post(
      `${BASE_PATH}/${connectionId}/auto-sync`,
      data,
    )
    return response.data
  },

  getSyncStatus: async (
    connectionId: string,
  ): Promise<SharepointSyncStatusResponse> => {
    const response = await apiClient.get(
      `${BASE_PATH}/${connectionId}/sync/status`,
    )
    return response.data
  },

  deleteAllSharepointFiles: async (): Promise<{
    deleted: number
    failed: number
    message: string
  }> => {
    const response = await apiClient.delete("/integrations/sharepoint/files")
    return response.data
  },
}

export const useGetSharepointConnection = () => {
  return useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: sharepointConnectionApi.getConnection,
    retry: false,
  })
}

export const useCreateSharepointConnection = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (data: CreateSharepointConnectionRequest) =>
      sharepointConnectionApi.createConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.INTEGRATIONS_MANAGE)
}

export const useDeleteSharepointConnection = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (connectionId: string) =>
      sharepointConnectionApi.deleteConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.INTEGRATIONS_MANAGE)
}

export const useAddFolderSelections = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (params: {
      connectionId: string
      folders: SharepointFolderSelection[]
      microsoftAccessToken: string
    }) =>
      sharepointConnectionApi.addFolderSelections(
        params.connectionId,
        params.folders,
        params.microsoftAccessToken,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.INTEGRATIONS_MANAGE)
}

export const useRemoveFolderSelection = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (params: { connectionId: string; selectionId: string }) =>
      sharepointConnectionApi.removeFolderSelection(
        params.connectionId,
        params.selectionId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.INTEGRATIONS_MANAGE)
}

export const useTriggerSync = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (params: { connectionId: string; data: TriggerSyncRequest }) =>
      sharepointConnectionApi.triggerSync(params.connectionId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.INTEGRATIONS_MANAGE)
}

export const useTriggerAutoSync = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (params: { connectionId: string; data: TriggerSyncRequest }) =>
      sharepointConnectionApi.triggerAutoSync(params.connectionId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.INTEGRATIONS_MANAGE)
}

export const useGetSyncStatus = (
  connectionId: string | undefined,
  enabled: boolean = false,
) => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ["sharepointSyncStatus", connectionId],
    queryFn: () => sharepointConnectionApi.getSyncStatus(connectionId!),
    enabled: enabled && !!connectionId,
    refetchInterval: (query) => {
      if (!enabled) return false

      const data = query.state.data
      // Stop polling when sync is completed or failed
      if (data?.status === "completed" || data?.status === "failed") {
        // Refetch connection to update isSyncing flag
        queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
        return false
      }

      return SHAREPOINT_CONFIG.SYNC_STATUS_POLL_INTERVAL
    },
  })
}

export const useDeleteAllSharepointFiles = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => sharepointConnectionApi.deleteAllSharepointFiles(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.INTEGRATIONS_MANAGE)
}
